import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Icon, Modal, Text } from '@Sage/hoi-poi-ui';
import { SectionListCrud } from '@Sage/ssm-web5';

import { CrudActions } from 'actions';
import { InfoAlert } from 'components/alerts';
import Button from 'components/Button';
import { DocumentsIcons } from 'components/icons';
import Literal from 'components/Literal';
import { CardHeaderWidget } from 'containers/components/detail/card';
import AddEntityDialog from 'containers/components/dialog/AddEntityDialog';
import { getLiteral } from 'utils/getLiteral';
import { formatDecimalByConfig, DECIMAL_TYPES, normalizeDecimalValue } from 'utils/decimal';
import { warningToast } from 'utils/toast';

import CONFIG from './TableConfig';
import FamilyAddDialogDecorator from './FamilyAddDialogDecorator';

import './styles.scss';

const MAX_FAMILIES = 200;

const deleteModalOverrides = {
    confirmButton: {
        type: 'primary-error',
    },
};

const FamilyAddDialogDecorated = FamilyAddDialogDecorator(AddEntityDialog);

// Helper to parse discount values
const parseDiscount = (value) => {
    const parsed = normalizeDecimalValue(value);
    return parsed == null ? 0 : parsed;
};

// Helper to compute total discount from individual discounts
const computeTotalDiscount = (d1, d2, d3, d4) => {
    const factor = (1 - d1 / 100) * (1 - d2 / 100) * (1 - d3 / 100) * (1 - d4 / 100);
    return (1 - factor) * 100;
};

// Helper to format total discount for display
const formatTotalDiscount = ({ value, idEnvironment, idCurrency }) => {
    const formatted = formatDecimalByConfig({
        value,
        type: DECIMAL_TYPES.GENERAL,
        idEnvironment,
        idCurrency,
        fieldId: 'totaldiscount',
    });
    return formatted ? `${formatted}%` : '-';
};

const EmptyView = memo(() => (
    <div className="family-rates-empty">
        <div className="family-rates-empty__content">
            <DocumentsIcons style={{ fontSize: 41, display: 'block', marginBottom: 16 }} />
            <Literal className="hintText" literal="error_no_familiesadded_rate" />
        </div>
    </div>
));

EmptyView.displayName = 'EmptyView';

// CRUD Schema for editable percentage fields
const crudSchema = [
    {
        id: 'ratediscount1',
        type: 'percentage',
        isMandatory: true,
        defaultValue: 0,
        postComponent: <span>%</span>,
    },
    {
        id: 'ratediscount2',
        type: 'percentage',
        isMandatory: false,
        defaultValue: 0,
        postComponent: <span>%</span>,
    },
    {
        id: 'ratediscount3',
        type: 'percentage',
        isMandatory: false,
        defaultValue: 0,
        postComponent: <span>%</span>,
    },
    {
        id: 'ratediscount4',
        type: 'percentage',
        isMandatory: false,
        defaultValue: 0,
        postComponent: <span>%</span>,
    },
];

const CrudFamilyRates = memo(({ field }) => {
    const dispatch = useDispatch();

    // ========================================
    // REDUX STATE & SELECTORS
    // ========================================
    const modelList = useSelector((state) => state.crud.entityCrud.get(field));
    const idCurrencyState = useSelector(
        (state) =>
            state.crud.entityCrud.get('idcurrency') || state.crud.entityCrud.get('idCurrency'),
    );
    const idEnvironmentState = useSelector(
        (state) =>
            state.crud.entityCrud.get('stridenvironment') ||
            state.crud.entityCrud.get('strIdEnvironment'),
    );

    // ========================================
    // DERIVED STATE & MEMOIZATION
    // ========================================
    const dataSize = modelList ? modelList.size : 0;

    // ========================================
    // LOCAL STATE
    // ========================================
    const tableRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [rowToDelete, setRowToDelete] = useState(null);

    // ========================================
    // EVENT HANDLERS & CALLBACKS
    // ========================================
    const onChange = useCallback(
        (fieldKey, value) => {
            dispatch(CrudActions.updateFieldCrud(fieldKey, value));
        },
        [dispatch],
    );

    const handleOnRef = useCallback((ref) => {
        tableRef.current = ref;
    }, []);

    const handleOpen = useCallback(() => {
        if (dataSize >= MAX_FAMILIES) {
            warningToast({
                text: getLiteral('label_price_list_exceeds_families'),
            });
            return;
        }
        setOpen(true);
    }, [dataSize]);

    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);

    const openDeleteDialog = useCallback((row) => {
        setRowToDelete(row);
        setDeleteDialogOpen(true);
    }, []);

    const closeDeleteDialog = useCallback(() => {
        setDeleteDialogOpen(false);
        setRowToDelete(null);
    }, []);

    const confirmDeleteLine = useCallback(() => {
        if (!modelList || !onChange || !rowToDelete) return;
        const rowIndex = modelList.findIndex((item) => item.id === rowToDelete.id);
        if (rowIndex !== -1) {
            const newModelList = modelList.delete(rowIndex);
            onChange(field, newModelList);
        }
        closeDeleteDialog();
    }, [modelList, onChange, field, rowToDelete, closeDeleteDialog]);

    const onChangeCell = useCallback(
        ({ rowIndex, field: fieldKey, value }) => {
            if (!modelList || !onChange) return;
            const model = modelList.get(rowIndex);
            if (!model) return;

            model[fieldKey] = value;
            const newModelList = modelList.set(rowIndex, model.immutable);
            onChange(field, newModelList);
        },
        [modelList, onChange, field],
    );

    const getRowNodeId = useCallback((data) => data.id, []);

    // Derive rowData from Redux state - compute totaldiscount ourselves to avoid model getter bugs
    const rowData = useMemo(() => {
        if (!modelList) return [];
        return modelList.toArray().map((model, index) => {
            // Read discount values directly from entity to avoid model getter bugs
            const d1 = parseDiscount(model.entity?.ratediscount1);
            const d2 = parseDiscount(model.entity?.ratediscount2);
            const d3 = parseDiscount(model.entity?.ratediscount3);
            const d4 = parseDiscount(model.entity?.ratediscount4);
            const total = computeTotalDiscount(d1, d2, d3, d4);

            return {
                id: model.id,
                familyname: model.familyname,
                numLine: index + 1,
                ratediscount1: d1,
                ratediscount2: d2,
                ratediscount3: d3,
                ratediscount4: d4,
                totaldiscount: formatTotalDiscount({
                    value: total,
                    idEnvironment: idEnvironmentState,
                    idCurrency: idCurrencyState,
                }),
            };
        });
    }, [idCurrencyState, idEnvironmentState, modelList]);

    const getColumnDefs = useCallback(
        ({ transformTableToCrud }) => {
            return new Promise((resolve) => {
                const config = CONFIG();
                let columnDefs = config.columnDefs;

                // Transform columns to CRUD mode using the provided function
                columnDefs = transformTableToCrud({
                    columnDefs,
                    crudSchema,
                    inputProps: {
                        onChange: onChangeCell,
                    },
                });

                resolve(columnDefs);
            });
        },
        [onChangeCell],
    );

    const actions = useMemo(
        () => [
            (row) => ({
                icon: <Icon name="close" onClick={() => openDeleteDialog(row)} />,
                tooltip: { content: getLiteral('label_remove_from_list') },
            }),
        ],
        [openDeleteDialog],
    );

    const { domLayout, extraClassName } = useMemo(() => {
        if (!rowData?.length) {
            return {
                domLayout: 'normal',
                extraClassName: 'family-rates-table--empty',
            };
        } else if (rowData.length <= 5) {
            return { domLayout: 'autoHeight', extraClassName: '' };
        }
        return {
            domLayout: 'normal',
            extraClassName: 'family-rates-table--scrollable',
        };
    }, [rowData]);

    const renderAddFamily = () => (
        <Button
            type="secondarySmall"
            text={<Literal literal="action_add" />}
            onClick={handleOpen}
        />
    );

    // ========================================
    // RENDER
    // ========================================
    return (
        <div className="familyRatesField crudTableField">
            <div className="sectionTitle">
                <i className="ic-arrow" />
                <Literal literal="label_per_families_rates" />
            </div>
            <div className="sectionSubtitle">
                <InfoAlert
                    iconDisplay={true}
                    text={<Literal literal="helptext_per_families_rates" />}
                />
                <div>
                    <Literal literal="label_info_families_rates" />
                </div>
            </div>

            <div className="editTableCard">
                <CardHeaderWidget
                    title={<Literal literal="label_family" />}
                    additionalinfo={` (${dataSize})`}
                    rightContent={renderAddFamily()}
                />
                {dataSize > 0 ? (
                    <SectionListCrud
                        id="family-rates-table"
                        className={`family-rates-table ${extraClassName}`}
                        getColumnDefs={getColumnDefs}
                        rowData={rowData}
                        getRowNodeId={getRowNodeId}
                        useSort={false}
                        domLayout={domLayout}
                        actions={actions}
                        onRef={handleOnRef}
                        forceRowUpdating
                    />
                ) : (
                    <EmptyView />
                )}
                <Modal
                    className="fm-delete-modal"
                    overlayClassName="fm-delete-modal__overlay"
                    overrides={deleteModalOverrides}
                    useCornerClose={false}
                    isOpen={deleteDialogOpen}
                    onCancel={closeDeleteDialog}
                    onConfirm={confirmDeleteLine}
                    title={getLiteral('label_remove_from_list')}
                    confirmText={getLiteral('action_confirm_bn')}
                    cancelText={getLiteral('action_cancel')}
                    size="small"
                >
                    <Text>{getLiteral('confirm_sure')}</Text>
                </Modal>
                <FamilyAddDialogDecorated
                    title={<Literal literal="action_search_families" />}
                    field={field}
                    handleClose={handleClose}
                    open={open}
                    entity={null}
                    familiesList={modelList}
                />
            </div>
        </div>
    );
});

CrudFamilyRates.displayName = 'CrudFamilyRates';

export default CrudFamilyRates;
